// PMTiles — OPFS-backed Source (Wave 14bd)
// =========================================
// Wir lesen PMTiles-Byte-Ranges direkt aus dem Origin Private File System
// (OPFS). Das ist der dritte und finale Iterationsschritt auf der Storage-
// Seite:
//
//   Wave 14ay und früher: Workbox rangeRequests-Plugin auf CacheStorage.
//     Problem: auf iOS Safari Vary:Range-Race, falsche Bytes bei Offline-Kalt-
//     start, FetchEvent-TypeErrors die die ganze Seite reissen.
//
//   Wave 14az..14bc: CacheStorage direkt auslesen (CacheStorageSource). Hat
//     die Vary/Workbox-Probleme eliminiert, aber einen fundamentalen Engpass
//     behalten: cache.match(url).blob() auf 120 MB Datei ist auf iOS Safari
//     ein 10-15 s Disk-Read, VOR dem ersten Tile. Auch nach Prewarm blieb
//     der Kaltstart zäh, weil Safari den Blob beim Tab-Reload wieder aus
//     dem Memory schmeisst und neu laden muss.
//
//   Wave 14bd (dieses Modul): OPFS-FileHandle. `getFile()` gibt eine `File`-
//     Referenz zurück — ohne Inhalt zu laden. `file.slice(a, b).arrayBuffer()`
//     liest nur die angeforderten Bytes von Disk. Keine volle Datei im RAM,
//     keine HTTP-Semantik, keine Workbox in der Nähe. Das reduziert den
//     "time to first tile" offline von ~10-30 s auf <100 ms.
//
// Was nicht mehr drin ist: der Fetch-Fallback.
// --------------------------------------------
// In 14bb hatten wir noch einen "online + cache miss → fetch()" Zweig. Der
// Kumpel hat zurecht darauf hingewiesen dass das eine verkappte Risikostelle
// ist: `navigator.onLine === true` heisst nicht "Server erreichbar" (captive
// portal, Wifi ohne Internet, Mobilfunk mit Loch, etc.) — der Fetch hängt
// trotz 10s-Timeout den Tile-Request, MapLibre rendert nichts, der User
// wartet. Die richtige Politik für eine "offline-by-default" App ist: wenn
// die Datei nicht im OPFS ist, werfen. MapLibre zeichnet die fehlende
// Source dann halt nicht — das ist ein ehrlicher Zustand und schneller als
// stilles Hängen. Der Download-Flow (FirstLaunchGate) ist der einzige Ort,
// an dem wir bewusst online gehen; alles andere soll strukturell offline-
// autark sein.
//
// Wave 14ca-fix3: Fetch-Fallback NUR im Non-Standalone-Modus.
// -----------------------------------------------------------
// Seit dem AuthGate→InstallGate-Reorder (Wave 14ca) können Non-Standalone-
// User (= reguläres Safari/Chrome ohne PWA-Install) die App tatsächlich
// erreichen — vorher hat der InstallGate sie davor vollständig blockiert.
// Im Non-Standalone-Modus skippt aber der FirstLaunchGate (seit 14bz) den
// Download → OPFS bleibt leer → strict-throw → blank canvas. Damit der
// Browser-Modus überhaupt navigierbar ist, fallen wir nur dann (NUR im
// Non-Standalone) auf HTTP-Range-Requests zurück. In Standalone bleibt
// die Wave-14bd-Strikt-Politik unverändert: kein Fetch-Fallback, leerer
// OPFS = blank ist ein echter Bug-Signal (Korruption, Eviction).

import maplibregl from 'maplibre-gl';
import { PMTiles, Protocol, type RangeResponse, type Source } from 'pmtiles';
import {
  OPFS_PMTILES_UPDATED_EVENT,
  opfsFilenameFromUrl,
  openPmtilesFile,
  type OpfsPmtilesUpdatedDetail,
} from '../opfsStore';

/**
 * OpfsPmtilesSource — liest PMTiles-Byte-Ranges aus einer im OPFS liegenden
 * Datei. Konstruiert mit derselben HTTP-URL, die MapLibre nach dem
 * `pmtiles://`-Prefix sieht (damit die Protocol-Lookup-Gleichheit stimmt);
 * der tatsächliche File-Read findet aber aus OPFS statt (Dateiname aus URL
 * abgeleitet).
 *
 * Concurrency: mehrere parallele getBytes() sollen sich den File-Handle-Load
 * teilen (sonst bekommen wir bei 20 parallelen Tile-Requests 20 Mal einen
 * getFile()-Call — billig, aber unnötig).
 */
/**
 * Detect ob die App als installierte PWA läuft (Standalone-Display-Mode oder
 * iOS' non-standardisiertes `navigator.standalone`). Bestimmt, ob im OPFS-
 * miss-Fall der Fetch-Fallback greift.
 */
function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return true;
  const iosStandalone =
    (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  const displayStandalone = window.matchMedia('(display-mode: standalone)').matches;
  return iosStandalone || displayStandalone;
}

class OpfsPmtilesSource implements Source {
  private file: File | null = null;
  private loadPromise: Promise<File> | null = null;
  /** Wave 14ca-fix3: einmal in den Network-Modus geschaltet, bleiben wir
   *  drin — nicht jeden Tile-Request neu OPFS probieren. */
  private networkMode = false;

  constructor(private readonly url: string) {
    // Wave 14ce-fix: auf OPFS-Updates lauschen. Wenn die Datei für unsere
    // URL via OfflinePage neu heruntergeladen / gelöscht / clear-all wurde,
    // werfen wir den gecachten `File`-Snapshot weg, sodass der nächste
    // getBytes()-Call einen frischen `getFile()` macht. Ohne diese
    // Invalidierung bleibt der Source bei der alten Pre-Download-Sicht
    // hängen → "Everest verschwindet nachdem ich Liguria-Map runtergeladen
    // habe", weil das alte PMTiles keine Everest-Tiles enthielt.
    if (typeof window !== 'undefined') {
      window.addEventListener(OPFS_PMTILES_UPDATED_EVENT, this.onOpfsUpdate);
    }
  }

  /** Window-Event-Handler: cleart die File-Cache wenn die OPFS-Datei
   *  geändert wurde (oder bei "alle clearen" via filename=null). Arrow-
   *  Funktion damit der `this`-Bind ohne `.bind(this)`-Boilerplate stabil
   *  bleibt.
   *
   *  Match per Dateiname (NICHT per voller URL), weil OfflinePage mit
   *  relativer Manifest-URL und MapView mit absoluter Origin-URL arbeitet.
   *  Beide haben aber denselben Dateinamen via opfsFilenameFromUrl. */
  private onOpfsUpdate = (event: Event) => {
    const detail = (event as CustomEvent<OpfsPmtilesUpdatedDetail>).detail;
    let myFilename: string;
    try {
      myFilename = opfsFilenameFromUrl(this.url);
    } catch {
      return;
    }
    if (detail.filename !== null && detail.filename !== myFilename) return;
    this.file = null;
    this.loadPromise = null;
    // networkMode bewusst NICHT zurücksetzen — wenn wir vorher auf
    // Network-Range-Requests umgeschaltet haben (Browser-Mode), waren wir
    // dort weil OPFS leer/kaputt war. Ein Re-Download könnte das geändert
    // haben, aber für Sicherheit lassen wir den Modus bestehen; ein
    // Tab-Reload reset es ohnehin.
    console.info(`[pmtiles] ${this.shortUrl()}: OPFS update event → cache invalidated`);
  };

  getKey(): string {
    return this.url;
  }

  async getBytes(offset: number, length: number, signal?: AbortSignal): Promise<RangeResponse> {
    if (this.networkMode) {
      return this.getBytesFromNetwork(offset, length, signal);
    }
    try {
      const file = await this.ensureFile(signal);
      if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
      // File.slice ist ein Blob.slice — disk-backed, keine Kopie. arrayBuffer()
      // liest nur die angeforderten Bytes. Auf iOS Safari messen wir <5 ms pro
      // Tile-Range, unabhängig von der Gesamt-Filegrösse.
      const slice = file.slice(offset, offset + length);
      const data = await slice.arrayBuffer();
      return { data };
    } catch (err) {
      // Wave 14ca-fix3: OPFS-Miss im Browser-Modus → Network-Range-Fallback.
      // Im Standalone bleibt strict (Wave 14bd): leerer OPFS ist dort ein
      // Bug-Signal, kein Fall-Back wert.
      if (signal?.aborted) throw err;
      if (!isStandaloneDisplay()) {
        console.warn(
          `[pmtiles] ${this.shortUrl()}: OPFS-Miss → switching to network range requests (browser mode)`,
        );
        this.networkMode = true;
        return this.getBytesFromNetwork(offset, length, signal);
      }
      throw err;
    }
  }

  /** HTTP-Range-Request gegen die Origin-URL der PMTiles-Datei. PMTiles-
   *  Format ist explizit für Range-Requests designed; jeder vernünftige
   *  Static-Host (Caddy/Nginx) macht das automatisch. */
  private async getBytesFromNetwork(
    offset: number,
    length: number,
    signal?: AbortSignal,
  ): Promise<RangeResponse> {
    const resp = await fetch(this.url, {
      headers: { Range: `bytes=${offset}-${offset + length - 1}` },
      signal,
      // CORS sollte hier nie ein Problem sein — gleiches Origin wie die App.
      // credentials: 'omit' wäre sicherer, aber unsere Tiles sind eh public.
    });
    // 206 Partial Content (Range honoured) ist der Erfolgsfall; manche
    // Hosts/CDNs antworten allerdings 200 wenn sie die Range ignorieren —
    // das wäre verschwendete Bandbreite, aber die Bytes wären trotzdem
    // korrekt slice-bar. Wir akzeptieren beides.
    if (!resp.ok && resp.status !== 206) {
      throw new Error(`pmtiles network fetch ${resp.status} ${this.shortUrl()}`);
    }
    const data = await resp.arrayBuffer();
    return { data };
  }

  private ensureFile(signal?: AbortSignal): Promise<File> {
    if (this.file) return Promise.resolve(this.file);
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = (async () => {
      try {
        const file = await openPmtilesFile(this.url);
        if (!file) {
          // Strikt: keine Datei im OPFS → kein Tile (im Standalone). Im
          // Non-Standalone fängt der getBytes-Catch das ab und switcht auf
          // Network — siehe oben.
          throw new Error(`pmtiles not in OPFS: ${this.url}`);
        }
        if (file.size <= 127) {
          console.error(
            `[pmtiles] ${this.shortUrl()}: OPFS-Datei zu klein (${file.size} B) — korrupt.`,
          );
          throw new Error(`pmtiles OPFS file too small: ${file.size} B`);
        }
        if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
        console.info(
          `[pmtiles] ${this.shortUrl()}: OPFS open (${formatMB(file.size)})`,
        );
        this.file = file;
        return file;
      } finally {
        this.loadPromise = null;
      }
    })();
    return this.loadPromise;
  }

  /** Kurzform der URL fürs Logging (nur der Dateiname). */
  private shortUrl(): string {
    try {
      const p = new URL(this.url).pathname;
      return p.split('/').pop() || this.url;
    } catch {
      return this.url;
    }
  }
}

let registeredProtocol: Protocol | null = null;
const registeredArchives = new Map<string, PMTiles>();

/**
 * Einmal am App-Start aufrufen. Registriert den 'pmtiles://'-Protocol
 * bei MapLibre und gibt die singleton Protocol-Instanz zurück.
 */
export function registerPmtilesProtocol(): Protocol {
  if (registeredProtocol) return registeredProtocol;
  registeredProtocol = new Protocol();
  maplibregl.addProtocol('pmtiles', registeredProtocol.tile);
  return registeredProtocol;
}

/**
 * Registriert ein PMTiles-Archiv das seine Bytes aus OPFS liest. MUSS
 * aufgerufen werden BEVOR MapLibre den ersten Tile-Request absetzt (also
 * bevor map.setStyle mit der pmtiles://-URL läuft).
 *
 * `archiveUrl` ist die absolute HTTP-URL der .pmtiles-Datei — exakt so wie
 * sie in den MapLibre-Source-URLs nach dem 'pmtiles://'-Prefix steht
 * (z.B. "https://staging.tomjanas.com/tiles/liguria.pmtiles"). Protocol
 * schlägt Archives per strikter String-Gleichheit nach, also muss der Key
 * 1:1 zu dem passen, was MapView.tsx in die Style-Sources schreibt.
 *
 * Idempotent — ein zweiter Aufruf mit gleicher URL ist ein No-Op.
 */
export function addPmtilesArchive(archiveUrl: string): void {
  const protocol = registerPmtilesProtocol();
  if (registeredArchives.has(archiveUrl)) return;
  const source = new OpfsPmtilesSource(archiveUrl);
  const archive = new PMTiles(source);
  protocol.add(archive);
  registeredArchives.set(archiveUrl, archive);
  console.info(`[pmtiles] archive registriert: ${archiveUrl}`);
}

/**
 * Pre-warm: triggert den initialen File-Handle-Load für jedes registrierte
 * Archive. Bei OPFS ist das günstig (nur getFileHandle + getFile, kein
 * Full-Read) — aber hilfreich, damit der allererste Tile-Render keinen
 * zusätzlichen Disk-Roundtrip im kritischen Pfad hat.
 */
export async function prewarmPmtilesArchives(): Promise<void> {
  const tasks: Promise<void>[] = [];
  for (const [url, archive] of registeredArchives.entries()) {
    tasks.push((async () => {
      try {
        // getHeader() triggert intern einen getBytes(0, 127) — das bringt
        // den OPFS-File-Handle in unseren OpfsPmtilesSource.
        await archive.getHeader();
        console.info(`[pmtiles] prewarm OK: ${shortUrlOf(url)}`);
      } catch (err) {
        console.warn(`[pmtiles] prewarm fehlgeschlagen: ${shortUrlOf(url)}`, err);
      }
    })());
  }
  await Promise.all(tasks);
}

/** Nur für Tests / Reset: wirft alle registrierten Archives weg. */
export function resetPmtilesArchives(): void {
  registeredArchives.clear();
}

/**
 * Wave 14cf — Direkter Zugriff auf eine registrierte PMTiles-Instanz.
 *
 * Beispiel-Use-Case: der DEM-Sampler (`pmtilesElevationSampler.ts`) braucht
 * direkte `getZxy()`-Calls auf den hillshade-PMTiles, ohne den Umweg über
 * MapLibre-Tile-Renderung (sonst hängt das Höhenprofil davon ab welche
 * Tiles gerade im Map-Viewport sind).
 *
 * Returns null wenn die URL nicht via `addPmtilesArchive` registriert wurde
 * (typisch wenn das Manifest die Hillshade-PMTiles nicht enthält oder OPFS
 * leer ist).
 */
export function getPmtilesArchive(archiveUrl: string): PMTiles | null {
  return registeredArchives.get(archiveUrl) ?? null;
}

/**
 * Liste aller registrierten Archive-URLs — nützlich für Diagnose und für
 * den DEM-Sampler der die passende Region-PMTiles auswählen will.
 */
export function listPmtilesArchives(): string[] {
  return Array.from(registeredArchives.keys());
}

function shortUrlOf(url: string): string {
  try {
    return new URL(url).pathname.split('/').pop() || url;
  } catch {
    return url;
  }
}

function formatMB(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}
